import { useState } from 'react';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, ArrowRight, Shield, Clock, Wrench, Truck, CheckCircle, RotateCcw, User, Calendar } from 'lucide-react';

const trustSignals = [
  { icon: Shield, title: '90-Day Warranty', desc: 'Full parts & labour coverage' },
  { icon: Clock, title: '4-7 Day Turnaround', desc: 'Quick, reliable repairs' },
  { icon: Wrench, title: 'Genuine Parts', desc: 'Manufacturer components only' },
  { icon: Truck, title: 'Free Collection', desc: 'Same-day pickup across London' },
];

const GAS_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxaG4ZQ2dc9wIJdHrfnoHDr70GUMPX3oknIwpbJFE3N-5nmVW5X8RLyrxsqpiYr8u-Liw/exec';

export default function BookRepairPage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', company: '',
    brand: '', model: '', serial_number: '', lamp_hours: '', error_code: '',
    occurrence: '', symptoms: '', preferred_contact: 'email',
    collection_addr: '', return_addr: '', consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [submittedData, setSubmittedData] = useState<typeof formData | null>(null);

  const generateRef = () => {
    const year = new Date().getFullYear();
    const num = String(Math.floor(Math.random() * 9000) + 1000);
    return `REQ-${year}-${num}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) {
      toast.error('Please consent to data processing to continue.');
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        brand: formData.brand,
        model: formData.model,
        serial_number: formData.serial_number,
        lamp_hours: formData.lamp_hours,
        error_code: formData.error_code,
        occurrence: formData.occurrence,
        symptoms: formData.symptoms,
        preferred_contact: formData.preferred_contact,
        collection_address: formData.collection_addr,
        return_address: formData.return_addr,
        consent: formData.consent === true,
        website_id: window.location.hostname || 'projectorrepairlondon.co.uk',
        source: window.location.hostname || 'projectorrepairlondon.co.uk',
        userAgent: navigator.userAgent,
      };

      const ref = generateRef();
      setRefNumber(ref);
      setSubmittedData({ ...formData });

      await fetch(GAS_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        mode: 'no-cors',
      });

      setSubmitted(true);
    } catch (err) {
      toast.error('Submission failed. Please try again or call us on 0333 006 4326.', {
        description: 'You can also email us at wellselectronicsltd@gmail.com',
        duration: 8000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "h-11 bg-white border-[#0057B7]/10 text-[#0057B7] focus:border-[#FFDD00] focus:ring-[#FFDD00]";
  const labelClass = "text-[#0057B7]/80 text-sm";
  const textareaClass = "min-h-[120px] bg-white border-[#0057B7]/10 text-[#0057B7]";

  const handleReset = () => {
    setSubmitted(false);
    setRefNumber('');
    setSubmittedData(null);
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', company: '',
      brand: '', model: '', serial_number: '', lamp_hours: '', error_code: '',
      occurrence: '', symptoms: '', preferred_contact: 'email',
      collection_addr: '', return_addr: '', consent: false,
    });
  };

  const brandLabel = (val: string) => {
    const map: Record<string, string> = {
      epson: 'Epson', optoma: 'Optoma', benq: 'BenQ', sony: 'Sony',
      panasonic: 'Panasonic', viewsonic: 'ViewSonic', nec: 'NEC', jvc: 'JVC',
      acer: 'Acer', hisense: 'Hisense', lg: 'LG', xiaomi: 'Xiaomi',
      wemax: 'Wemax', 'leica-cine': 'Leica Cine', formovie: 'Formovie',
      vava: 'VAVA', 'awol-vision': 'AWOL Vision', casiris: 'Casiris',
      nexigo: 'Nexigo', ultimea: 'Ultimea', christie: 'Christie',
      sanyo: 'Sanyo', vivitek: 'Vivitek', dhn: 'DHN', barco: 'Barco',
      hitachi: 'Hitachi', boxlight: 'Boxlight', other: 'Other / Not Sure',
    };
    return map[val] || val;
  };

  return (
    <>
      <SEO title="Submit a Repair Enquiry" description="Submit a repair enquiry for your projector. Free collection across London. Expert diagnostics for DLP, LCD, laser and xenon projectors. 90-day warranty." canonical="https://projectorrepairlondon.co.uk/repair-enquiry" />

      {/* Hero - DARK */}
      <section className="bg-[#0057B7] pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="container-custom">
          <nav className="mb-6"><ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/40 hover:text-white transition-colors">Home</Link></li><li className="text-white/20">/</li><li className="text-white font-medium">Repair Enquiry</li>
          </ol></nav>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white">
            {submitted ? 'Enquiry Submitted' : 'Submit a Repair Enquiry'}
          </h1>
          <p className="mt-4 text-white/50 max-w-xl">
            {submitted
              ? 'Your repair enquiry has been received. Our team will contact you shortly.'
              : 'Fill in the details below and our team will arrange free collection and a diagnostic assessment.'}
          </p>
        </div>
      </section>

      {/* Confirmation or Form - WHITE */}
      <section className="bg-white py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
            <div className="lg:col-span-3">
              {submitted && submittedData ? (
                <div className="space-y-8">
                  {/* Reference Number Banner */}
                  <div className="bg-[#FFDD00]/10 border border-[#FFDD00]/30 rounded-xl p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-[#0057B7] mx-auto mb-3" />
                    <p className="text-sm text-[#0057B7]/70 mb-1">Your enquiry has been submitted successfully</p>
                    <p className="text-xs text-[#0057B7]/50 mb-3">A copy has been emailed to you</p>
                    <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#0057B7] rounded-lg">
                      <span className="text-white/60 text-sm">Reference ID:</span>
                      <span className="text-[#FFDD00] font-mono font-bold text-xl tracking-wider">{refNumber}</span>
                    </div>
                    <p className="text-xs text-[#0057B7]/40 mt-3">Please quote this number in all correspondence</p>
                  </div>

                  {/* Submitted Details Summary */}
                  <div className="bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/5">
                    <h3 className="font-heading font-semibold text-[#0057B7] mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#0057B7]" /> Enquiry Summary
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Name</span><span className="text-[#0057B7] font-medium">{submittedData.first_name} {submittedData.last_name}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Email</span><span className="text-[#0057B7] font-medium">{submittedData.email}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Phone</span><span className="text-[#0057B7] font-medium">{submittedData.phone}</span></div>
                      {submittedData.company && <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Company</span><span className="text-[#0057B7] font-medium">{submittedData.company}</span></div>}
                      <div className="border-t border-[#0057B7]/10 pt-3 mt-3" />
                      <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Brand</span><span className="text-[#0057B7] font-medium">{brandLabel(submittedData.brand)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Model</span><span className="text-[#0057B7] font-medium">{submittedData.model}</span></div>
                      {submittedData.serial_number && <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Serial Number</span><span className="text-[#0057B7] font-medium">{submittedData.serial_number}</span></div>}
                      {submittedData.error_code && <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Error Code</span><span className="text-[#0057B7] font-medium">{submittedData.error_code}</span></div>}
                      {submittedData.occurrence && <div className="flex justify-between text-sm"><span className="text-[#0057B7]/50">Fault Timing</span><span className="text-[#0057B7] font-medium">{submittedData.occurrence}</span></div>}
                      <div className="border-t border-[#0057B7]/10 pt-3 mt-3" />
                      <div className="flex justify-between text-sm items-start"><span className="text-[#0057B7]/50 shrink-0">Symptoms</span><span className="text-[#0057B7] font-medium text-right max-w-[60%]">{submittedData.symptoms}</span></div>
                      {submittedData.collection_addr && <div className="flex justify-between text-sm items-start"><span className="text-[#0057B7]/50 shrink-0">Collection</span><span className="text-[#0057B7] font-medium text-right max-w-[60%]">{submittedData.collection_addr}</span></div>}
                    </div>
                  </div>

                  {/* What Happens Next */}
                  <div className="bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/5">
                    <h3 className="font-heading font-semibold text-[#0057B7] mb-4 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#0057B7]" /> What Happens Next
                    </h3>
                    <ol className="space-y-3">
                      {[
                        'We contact you within 24 hours to confirm your enquiry',
                        'Arrange free collection from your address',
                        'Complete diagnostic assessment at our workshop',
                        'Receive a detailed repair quote via email',
                        'Repair carried out with 90-day warranty',
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-[#0057B7]/70">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FFDD00]/20 text-[#0057B7] text-xs font-medium shrink-0 mt-0.5">{i + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-4">
                    <button onClick={handleReset} className="btn-primary">
                      <RotateCcw className="w-4 h-4" /> Submit Another Enquiry
                    </button>
                    <Link to="/" className="btn-outline">Back to Home</Link>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>First Name *</Label><Input name="first_name" value={formData.first_name} onChange={handleChange} required placeholder="John" className={inputClass} /></div>
                  <div className="space-y-2"><Label className={labelClass}>Last Name *</Label><Input name="last_name" value={formData.last_name} onChange={handleChange} required placeholder="Smith" className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>Email *</Label><Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="name@company.co.uk" className={inputClass} /></div>
                  <div className="space-y-2"><Label className={labelClass}>Phone *</Label><Input name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="07700 900123" className={inputClass} /></div>
                </div>
                <div className="space-y-2"><Label className={labelClass}>Company (optional)</Label><Input name="company" value={formData.company} onChange={handleChange} placeholder="Company name" className={inputClass} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>Projector Brand *</Label>
                    <select name="brand" value={formData.brand} onChange={handleChange} required className="w-full h-11 px-3 border border-[#0057B7]/10 rounded-lg text-sm bg-white text-[#0057B7] focus:border-[#FFDD00] focus:outline-none">
                      <option value="">Select brand</option>
                      <optgroup label="Mainstream Brands">
                        <option value="epson">Epson</option>
                        <option value="optoma">Optoma</option>
                        <option value="benq">BenQ</option>
                        <option value="sony">Sony</option>
                        <option value="panasonic">Panasonic</option>
                        <option value="viewsonic">ViewSonic</option>
                        <option value="nec">NEC</option>
                        <option value="jvc">JVC</option>
                        <option value="acer">Acer</option>
                      </optgroup>
                      <optgroup label="Ultra Short Throw">
                        <option value="hisense">Hisense</option>
                        <option value="lg">LG</option>
                        <option value="xiaomi">Xiaomi</option>
                        <option value="wemax">Wemax</option>
                        <option value="leica-cine">Leica Cine</option>
                        <option value="formovie">Formovie</option>
                        <option value="vava">VAVA</option>
                        <option value="awol-vision">AWOL Vision</option>
                        <option value="casiris">Casiris</option>
                        <option value="nexigo">Nexigo</option>
                        <option value="ultimea">Ultimea</option>
                      </optgroup>
                      <optgroup label="Commercial/Installation">
                        <option value="christie">Christie</option>
                        <option value="sanyo">Sanyo</option>
                        <option value="vivitek">Vivitek</option>
                        <option value="dhn">DHN</option>
                        <option value="barco">Barco</option>
                        <option value="hitachi">Hitachi</option>
                        <option value="boxlight">Boxlight</option>
                      </optgroup>
                      <option value="other">Other / Not Sure</option>
                    </select>
                  </div>
                  <div className="space-y-2"><Label className={labelClass}>Model *</Label><Input name="model" value={formData.model} onChange={handleChange} required placeholder="e.g. EB-U05" className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>Serial Number</Label><Input name="serial_number" value={formData.serial_number} onChange={handleChange} placeholder="Serial number" className={inputClass} /></div>
                  <div className="space-y-2"><Label className={labelClass}>Lamp Hours</Label><Input name="lamp_hours" value={formData.lamp_hours} onChange={handleChange} placeholder="e.g. 2500" className={inputClass} /></div>
                </div>
                <div className="space-y-2"><Label className={labelClass}>Error Code (if displayed)</Label><Input name="error_code" value={formData.error_code} onChange={handleChange} placeholder="e.g. Temp flashing / Red LED" className={inputClass} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>When did the fault start?</Label>
                    <select name="occurrence" value={formData.occurrence} onChange={handleChange} className="w-full h-11 px-3 border border-[#0057B7]/10 rounded-lg text-sm bg-white text-[#0057B7] focus:border-[#FFDD00] focus:outline-none">
                      <option value="">Select</option>
                      <option value="Startup only">Startup only</option>
                      <option value="After 10-30 minutes">After 10-30 minutes</option>
                      <option value="After 1+ hours">After 1+ hours</option>
                      <option value="Random / intermittent">Random / intermittent</option>
                      <option value="Constant from power on">Constant from power on</option>
                      <option value="Not sure">Not sure</option>
                    </select>
                  </div>
                  <div className="space-y-2"><Label className={labelClass}>Preferred Contact</Label>
                    <select name="preferred_contact" value={formData.preferred_contact} onChange={handleChange} className="w-full h-11 px-3 border border-[#0057B7]/10 rounded-lg text-sm bg-white text-[#0057B7] focus:border-[#FFDD00] focus:outline-none">
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2"><Label className={labelClass}>Symptoms / Fault Description *</Label><Textarea name="symptoms" value={formData.symptoms} onChange={handleChange} required placeholder="Describe the symptoms your projector is showing..." className={textareaClass} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label className={labelClass}>Collection Address</Label><Input name="collection_addr" value={formData.collection_addr} onChange={handleChange} placeholder="Address for projector collection" className={inputClass} /></div>
                  <div className="space-y-2"><Label className={labelClass}>Return Address (if different)</Label><Input name="return_addr" value={formData.return_addr} onChange={handleChange} placeholder="Leave blank if same as collection" className={inputClass} /></div>
                </div>
                <div className="bg-[#E8F0FE] rounded-xl p-5 border border-[#0057B7]/5">
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-[#0057B7]" />
                    <div>
                      <p className="text-sm font-semibold text-[#0057B7]">Free Collection & Return</p>
                      <p className="text-xs text-[#0057B7]/70">We collect your projector from your address and return it after repair at no extra cost.</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="consent" name="consent" checked={formData.consent} onChange={handleChange} required className="w-4 h-4 rounded border-[#0057B7]/20 text-[#0057B7]" />
                  <Label htmlFor="consent" className="text-[#0057B7]/70 text-xs cursor-pointer">I consent to having my data processed for this repair enquiry.</Label>
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary disabled:opacity-50">{isSubmitting ? 'Submitting...' : 'Submit Repair Enquiry'} <ArrowRight className="w-4 h-4" /></button>
              </form>
              )}
            </div>

            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="bg-gradient-to-br from-[#FFDD00] to-[#C9A800] rounded-xl p-6 text-white">
                  <h3 className="font-heading font-semibold text-lg mb-2">Prefer to call?</h3><p className="text-sm text-white/70 mb-4">Speak directly with our repair team.</p>
                  <a href="tel:+443330064326" className="flex items-center gap-3 py-3 px-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"><Phone className="w-5 h-5" /><div><p className="font-semibold">0333 006 4326</p><p className="text-xs text-white/50">Mon - Fri, 9AM - 5PM</p></div></a>
                </div>
                <div className="bg-[#E8F0FE] rounded-xl p-6">
                  <h3 className="font-label text-[#0057B7]/70 mb-5">Why Choose Us</h3>
                  <div className="space-y-4">{trustSignals.map(s => (<div key={s.title} className="flex items-start gap-3"><div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FFDD00]/10 text-[#0057B7] shrink-0"><s.icon className="w-4 h-4" /></div><div><p className="text-sm font-medium text-[#0057B7]">{s.title}</p><p className="text-xs text-[#0057B7]/60">{s.desc}</p></div></div>))}</div>
                </div>
                <div className="bg-[#E8F0FE] rounded-xl p-6">
                  <h3 className="font-label text-[#0057B7]/70 mb-4">What Happens Next</h3>
                  <ol className="space-y-3">{['We contact you within 24 hours', 'Arrange free collection from your location', 'Complete diagnostic assessment', 'Receive detailed repair quote', 'Repair with 90-day warranty'].map((step, i) => (<li key={i} className="flex items-start gap-3 text-sm text-[#0057B7]/70"><span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FFDD00]/20 text-[#0057B7] text-xs font-medium shrink-0 mt-0.5">{i + 1}</span>{step}</li>))}</ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}